import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {FormsModule} from '@angular/forms';
import {
  AppConfigService,
  AuthCodeResolver,
  Base64Encoder,
  BrowserWindowRef,
  InMemoryResourceCache,
  KeyValueStore,
  LifeislifeDomainModule,
  LocalStorageKeyValueStoreService,
  OauthStorageService,
  ResourceCache,
  UserAuthService,
  UserAuthServiceBrowser,
  WebBase64EncoderService,
  WindowRef,
} from '@lifeislife/lifeislife-domain';
import {HttpClientModule} from '@angular/common/http';
import {CustomerFrontErrorHandler} from './error.handler';
import {RouterModule} from '@angular/router';
import {ServiceWorkerModule} from '@angular/service-worker';
import {environment} from '../environments/environment';
import {UserLoggedInRouterGuard} from './user-logged-in.guard';
import {UserLoggedInRouterGuardBrowser} from './user-logged-in.browser-guard';
import {
  AppHomeScreenInstallerBrowserService,
  AppHomeScreenInstallerService,
  InsuranceComponentsModule,
  InsurancePartnersModule,
  LifeislifeFooterModule,
  StoredFileModule,
  TrusteeLogoModule,
  WebPushRegistrationService,
  WebPushRegistrationServiceBrowser,
} from '@lifeislife/lifeislife-components';
import {CommonModule} from '@angular/common';
import {InputTextModule} from 'primeng/inputtext';
import {LoginRouteComponent} from './app-shell/login-route/login-route.component';
import {ProductsRouteComponent} from './products-route/products-route.component';
import {FamilyProductListComponent} from './products-route/family-product-list/family-product-list.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {PaginatorModule} from 'primeng/paginator';
import {SharedComponentsModule} from './shared-components/shared-components.module';
import {MainPricingModule} from '../../../lifeislife-front/src/app/main/components/pricing/main-pricing.module';
import {CommonPipesModule} from '../../../lifeislife-front/src/app/commons/pipes/common-pipes.module';
import {CustomerContextService} from './service/customer-context-service';
import {CustomerContextServiceBrowser} from './service/customer-context-service.browser';
import {LoggedUserMenuComponent} from './app-shell/logged-user-menu/logged-user-menu.component';
import {CustomerContactItemComponent} from './app-shell/customer-contact-item/customer-contact-item.component';
import {CustomerContactSelectModalComponent} from './app-shell/customer-contact-select-modal/customer-contact-select-modal.component';
import {AppShellComponent} from './app-shell/app-shell.component';
import {OverlayPanelModule} from 'primeng/overlaypanel';
import {SharedModule} from 'primeng/api';
import {ProductFamilyRouteComponent} from './products-route/product-family-route/product-family-route.component';
import {UserPasswordModelComponent} from './app-shell/user-password-model/user-password-model.component';
import {ProductFamilyRequestPanelComponent} from './products-route/product-family-request-panel/product-family-request-panel.component';
import {DialogModule} from 'primeng/dialog';
import {InputTextareaModule} from 'primeng/inputtextarea';
import {ShellBannersComponent} from './app-shell/shell-banners/shell-banners.component';
import {OAuthModule, OAuthStorage} from 'angular-oauth2-oidc';
import {ItsmeLoginBlocComponent} from './app-shell/login-route/itsme-login-bloc/itsme-login-bloc.component';
import {MailLinkBlocComponent} from './app-shell/login-route/mail-link-bloc/mail-link-bloc.component';
import {PasswordBlocComponent} from './app-shell/login-route/password-bloc/password-bloc.component';
import {FamilyProductContentDirective} from './products-route/family-product-list/family-product-content.directive';
import {FamilyProductSubtitleDirective} from './products-route/family-product-list/family-product-subtitle.directive';
import {InputSwitchModule} from 'primeng/inputswitch';
import {PensionProductsFamilyRouteComponent} from './products-route/pension-products-family-route/pension-products-family-route.component';
import {FileNotFoundComponent} from './app-shell/file-not-found/file-not-found.component';
import {PackageSignedRouteComponent} from './app-shell/package-signed-route/package-signed-route.component';
import {RadioButtonModule} from 'primeng/radiobutton';
import {MandateRouteComponent} from './mandate-route/mandate-route.component';
import {InsuranceMandateRequestListItemComponent} from './mandate-route/insurance-mandate-request-list-item/insurance-mandate-request-list-item.component';
import {InsuranceMandateRequestItemComponent} from './mandate-route/insurance-mandate-request-item/insurance-mandate-request-item.component';
import {InsuranceRequestDraftDialogComponent} from './products-route/insurance-request-draft-dialog/insurance-request-draft-dialog.component';
import {ShellMessagesComponent} from './app-shell/shell-messages/shell-messages.component';
import {FamilyProductSubheaderDirective} from './products-route/family-product-list/family-product-subheader-.directive';
import {PensionReservePreviewModule} from './pension/pension-reserve-preview/pension-reserve-preview.module';
import {ContactPensionPreviewModule} from './pension/contact-pension-preview/contact-pension-preview.module';
import {TabViewModule} from 'primeng/tabview';
import {InsuranceSubscriptionItemModule} from './insurance/insurance-subscription-item/insurance-subscription-item.module';

export function AppConfigInitFactory(service: AppConfigService) {
  const factory = (): Promise<any> => {
    return service.getInitialized$().toPromise();
  };
  return factory;
}

@NgModule({
  declarations: [
    AppComponent,
    AppShellComponent,
    CustomerContactSelectModalComponent,
    CustomerContactItemComponent,
    LoggedUserMenuComponent,
    LoginRouteComponent,
    ProductsRouteComponent,
    FamilyProductListComponent,
    ProductFamilyRouteComponent,
    UserPasswordModelComponent,
    ProductFamilyRequestPanelComponent,
    ShellBannersComponent,
    ItsmeLoginBlocComponent,
    MailLinkBlocComponent,
    PasswordBlocComponent,
    FamilyProductContentDirective,
    FamilyProductSubtitleDirective,
    PensionProductsFamilyRouteComponent,
    FileNotFoundComponent,
    PackageSignedRouteComponent,
    InsuranceMandateRequestItemComponent,
    InsuranceMandateRequestListItemComponent,
    MandateRouteComponent,
    InsuranceRequestDraftDialogComponent,
    ShellMessagesComponent,
    FamilyProductSubheaderDirective,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {enabled: environment.production}),
    OAuthModule.forRoot(),
    LifeislifeDomainModule.forRoot(),
    AppRoutingModule,
    CommonModule,
    FormsModule,
    BrowserAnimationsModule,
    HttpClientModule,
    LifeislifeFooterModule,
    RouterModule,
    InputTextModule,
    PaginatorModule,
    SharedComponentsModule,
    MainPricingModule,
    TrusteeLogoModule,
    CommonPipesModule,
    OverlayPanelModule,
    SharedModule,
    DialogModule,
    InputTextareaModule,
    StoredFileModule,
    InputSwitchModule,
    RadioButtonModule,
    InsuranceComponentsModule,
    InsurancePartnersModule,
    PensionReservePreviewModule,
    ContactPensionPreviewModule,
    TabViewModule,
    InsuranceSubscriptionItemModule,
  ],
  providers: [
    {provide: APP_INITIALIZER, useFactory: AppConfigInitFactory, deps: [AppConfigService], multi: true},
    {provide: KeyValueStore, useClass: LocalStorageKeyValueStoreService},
    {provide: Base64Encoder, useClass: WebBase64EncoderService},
    {provide: ErrorHandler, useClass: CustomerFrontErrorHandler},
    {provide: UserLoggedInRouterGuard, useClass: UserLoggedInRouterGuardBrowser},
    {provide: WindowRef, useClass: BrowserWindowRef},
    {provide: ResourceCache, useClass: InMemoryResourceCache},
    {provide: AppHomeScreenInstallerService, useClass: AppHomeScreenInstallerBrowserService},

    {provide: UserAuthService, useClass: UserAuthServiceBrowser},
    {provide: CustomerContextService, useClass: CustomerContextServiceBrowser},
    {provide: OAuthStorage, useClass: OauthStorageService},
    {provide: WebPushRegistrationService, useClass: WebPushRegistrationServiceBrowser},
    AuthCodeResolver,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
